import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { getChatPath, getRoutesForRoutingComponent } from '../../routes';
import { Redirect, Route, useLocation } from 'react-router-dom';
import AppBar from '../../components/AppBar/AppBar';
import CustomBottomNavigation from './BottomNavigation';
import { theme } from '../../theme';
import MenuDrawer from './MenuDrawer';
import {
  getExistingTokenIfGranted,
  requestNotificationPermission,
  initializeFirebase,
  attachSwRefreshListener,
  subscribePushBridge,
  subscribeForegroundFCM,
} from '../../helpers/firebaseServices';
import { supportsWebPush } from '../../helpers/pushSupport';

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RouteContent = styled.div`
  padding-left: ${theme.paddings.appPaddingPxVal}px;
  padding-right: ${theme.paddings.appPaddingPxVal}px;
  overflow: ${(props) => (props.isBottomMenu ? 'hidden' : 'auto')};
  position: absolute;
  top: ${theme.sizes.navbarHeightPxVal}px;
  bottom: ${theme.sizes.bottomBarHeightPxVal}px;
  left: 0;
  right: 0;
`;

const BottomNavigationSpace = styled.div`
  height: 60px;
`;

function Layout(props) {
  const { appTitle = '', history, getMenuBadges, menuBadges, signOut, postRegisterDevice } = props;

  const fullModalRef = useRef(null);
  const modalRef = useRef(null);
  const [openFullModal, setOpenFullModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [bottomNavigationValue, _setBottomNavigationValue] = useState(0);
  const [onAppBarButtonClick, setOnAppBarButtonClick] = useState({
    onClick: () => {},
  });
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const [showGlobalLoadingIndicator, setShowGlobalLoadingIndicator] = useState(false);
  const [notificationToken, setNotificationToken] = useState(null);
  const [showEnableNotificationsButton, setShowEnableNotificationsButton] = useState(false);
  const location = useLocation();

  let routingElements = [];
  routingElements = routingElements.length === 0 ? getRoutesForRoutingComponent() : routingElements;

  const setBottomNavigationValue = ({ value = 0, bottomNavigationRoutes = [], noHistoryPush }) => {
    const selectedOption = bottomNavigationRoutes[value];
    if (selectedOption?.path && !noHistoryPush) history.push(selectedOption.path);
    _setBottomNavigationValue(value);
  };

  const handleEnableNotifications = async () => {
    try {
      const token = await requestNotificationPermission();
      if (token) {
        setNotificationToken(token);
        setShowEnableNotificationsButton(false);
        postRegisterDevice({ registration_id: token });
      }
    } catch (error) {
      console.error('[Layout] Failed to enable notifications:', error);
    }
  };
  
  useEffect(() => {
    getMenuBadges();
    const fetchBadgesInterval = setInterval(() => {
      getMenuBadges();
    }, 15 * 1000);

    initializeFirebase();
    let unsubSW = () => {};
    let unsubFG = () => {};

    // Listen for subscription changes and refresh token
    attachSwRefreshListener((newToken) => {
      console.log("[Layout] Refreshed token after subscription change");
      postRegisterDevice({ registration_id: newToken });
    });

    // One central push subscription → refresh badges + notify chat views
    const onBridgeEvent = (msg) => {
      if (msg?.type === 'PUSH_CHAT') {
        // 1) refresh badges immediately
        if (typeof getMenuBadges === 'function') {
          try { getMenuBadges(); } catch {}
        }
        // 2) broadcast to chat components so they can refetch
        window.dispatchEvent(new CustomEvent('chat:new', { detail: msg.data }));
      }
      if (msg?.type === 'FCM_REFRESH_TOKEN') {
        // Pull a fresh token and register
        getExistingTokenIfGranted().then((t) => {
          if (t) {
            try { postRegisterDevice({ registration_id: t }); } catch {}
          }
        });
      }
    };

    // Only attempt if web push is supported...
    if (supportsWebPush()) {
      getExistingTokenIfGranted().then((token) => {
        if (token) {
          setNotificationToken(token);
          postRegisterDevice({ registration_id: token });
        } else if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          // Show button if permission hasn't been decided yet
          setShowEnableNotificationsButton(true);
        }
      }).catch((error) => {
        console.warn('[Layout] getExistingTokenIfGranted failed:', error);
      });
      // Subscribe to SW bridge (and optional FCM foreground) events
      unsubSW = subscribePushBridge(onBridgeEvent);
      unsubFG = subscribeForegroundFCM(onBridgeEvent);
    }

    return () => {
      clearInterval(fetchBadgesInterval);
      try { unsubSW(); } catch {}
      try { unsubFG(); } catch {}
    };
  }, []);

  useEffect(() => {
    if (!location?.pathname.includes(`${getChatPath()}/`)) {
      window.scrollTo(0, 0);
    }
  }, [location?.pathname]);
  
  return (
    <div>
      {supportsWebPush() && showEnableNotificationsButton && (
        <button
          onClick={handleEnableNotifications}
          style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            zIndex: 1000,
            padding: '10px 20px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          Enable Notifications
        </button>
      )}
      {routingElements.map((route, idx) => {
        return route.component ? (
          <Route
            key={idx}
            path={route.path}
            exact={route.exact}
            name={route.name}
            render={(itemProps) => (
              <ContentContainer className="content-container">
                <AppBar
                  elevation={0}
                  title={appTitle}
                  onAppBarButtonClick={onAppBarButtonClick}
                  setIsMenuDrawerOpen={setIsMenuDrawerOpen}
                  showGlobalLoadingIndicator={showGlobalLoadingIndicator}
                  {...route?.appBar}
                />
                <MenuDrawer
                  isMenuDrawerOpen={isMenuDrawerOpen}
                  setIsMenuDrawerOpen={setIsMenuDrawerOpen}
                  signOut={signOut}
                />
                <RouteContent className="route-content" isBottomMenu={itemProps.isBottomMenu}>
                  <route.component
                    {...itemProps}
                    fullModalRef={fullModalRef}
                    openFullModal={openFullModal}
                    setOpenFullModal={setOpenFullModal}
                    modalRef={modalRef}
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    setOnAppBarButtonClick={setOnAppBarButtonClick}
                    isMenuDrawerOpen={isMenuDrawerOpen}
                    setIsMenuDrawerOpen={setIsMenuDrawerOpen}
                    setShowGlobalLoadingIndicator={setShowGlobalLoadingIndicator}
                  />
                </RouteContent>
              </ContentContainer>
            )}
          />
        ) : null;
      })}
      <CustomBottomNavigation
        bottomNavigationValue={bottomNavigationValue}
        setBottomNavigationValue={setBottomNavigationValue}
        menuBadges={menuBadges}
      />
      <BottomNavigationSpace />
    </div>
  );
}

export default Layout;